import { inject, observer } from "mobx-react";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog, Heading, Icon, Text, Modal } from "src/components";
import { Badge } from "src/components/badge";
import { theme } from "src/config";
import { Space, Tag, User } from "src/models";
import { getRoutePath, MatchParams, Page, RoomView } from "src/routes";
import { AuthStore, RoutingStore, SpaceStore, TagStore, UserStore } from "src/stores";
import { SpaceType, UserRole, UserStatus, UserType,  } from "src/types";
import { without } from "src/util/util";
import styled from "styled-components";
import { GroupCard, RoomCard, TagCard, UserCard } from "./cards";
import { Section } from "./section";


export interface IRoomProps{
  authStore?:    AuthStore,
  userStore?:    UserStore,
  spaceStore?:   SpaceStore,
  tagStore?:     TagStore,
  routingStore?: RoutingStore,
  params?:       MatchParams,
}

const Container = styled.div`
  padding: 0 2rem;
`;


export const RoomSettings = inject("authStore", "userStore", "spaceStore", "routingStore", "tagStore")(observer((props: IRoomProps): JSX.Element => {
  const { authStore, userStore, spaceStore, routingStore, tagStore } = props;
  const { t, i18n } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
  const toLink = React.useRef<number[]>([]);

  const rooms:         Space[] = spaceStore.getItemsByParent(authStore.getCurRoomID,    SpaceType.Room);
  const projRooms:     Space[] = spaceStore.getItemsByParent(authStore.getCurProjectID, SpaceType.Room).filter(r => r.id !== authStore.getCurRoomID && !rooms.includes(r));
  const roomUsers:     User[]  = userStore.getUsersBySpace(authStore.getCurRoomID,    [UserType.Default, UserType.Accountless]);
  const projectUsers:  User[]  = userStore.getUsersBySpace(authStore.getCurProjectID, [UserType.Default, UserType.Accountless]);
  const roomGroups:    User[]  = userStore.getUsersBySpace(authStore.getCurRoomID,    [UserType.Group]);
  const tags:     Tag[]   = tagStore.getTagsNotInSpace(authStore.getCurRoomID);
  const roomTags: Tag[]   = tagStore.getRoomTags;
  // const projectGroups: User[]  = userStore.getUsersBySpace(authStore.getCurProjectID, [UserType.Group]);

  const room = spaceStore.getItemByID(authStore.getCurRoomID);

  const closeCards = React.useCallback(() => {
    spaceStore.setActive(null);
    tagStore.setActive(null);
    userStore.setActive(null);
  }, []);

  React.useEffect(() => {
    closeCards();
  }, [])

  const currentUser = authStore.user;
  if( !currentUser.canEdit ) return null;

  return (
    <Container>
      <Heading type="h3">{room && room.title}</Heading>
      {/* Member Section */}
      <Section
        currentUser   = {currentUser}
        data          = {roomGroups}
        space_id      = {authStore.getCurRoomID}
        addData       = {[]}
        dataAlt       = {roomUsers}
        addDataAlt    = {without(projectUsers, roomUsers)}
        title         = {t("room_user_plural")}
        linkTitle     = {t("available_room_user_plural")}
        newToolTipAlt = {t("tooltip_new_room_member")}
        newIcon       = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="group" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        newToolTip    = {t("tooltip_new_group")}
        newIconAlt    = {<Badge border="2px"  height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="person" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        Card          = {GroupCard}
        CardAlt       = {UserCard}
        onNew         = {async () => {
          try {
            await userStore.createGroup(`${t("new_group")}`, authStore.getCurRoomID);
            toast.success(t('new_group_created'))
          } catch ( error ) {
            toast.error(t('failed_to_create_group'))
          }
        }}
        onNewAlt = {() => {
          routingStore.push(getRoutePath(Page.UserInvite, {view: "room", id: authStore.getCurRoomID}))
        }}
        onAdd   = {async (group: User) => {
          try {
            await userStore.createGroup(group.first_name, authStore.getCurRoomID);
            toast.success(t('new_group_created'))
          } catch (err) {
            toast.error(t('failed_added_group_project'))
          }
        }}
        onRemove = {async (group: User) => {
          try {
            await group.unlinkSpace(authStore.getCurRoomID);
            toast.success(t('removed_group_room'));
          } catch (err) {
            toast.error(t('failed_removed_group_room'))
          }
        }}
        onAddAlt    = {async (user: User) => {
          try {
            let   role   = user.getWorkSpaceSettings?.role   || UserRole.Member;
            const status = user.getWorkSpaceSettings?.status || UserStatus.Invited;
            if( role == UserRole.Owner ) role = UserRole.Leader;
            await user.linkSpace(authStore.getCurRoomID, SpaceType.Room, role, status);
            toast.success(t('added_user_room'));
          } catch (err) {
            console.error("Failed to add user to room", err);
            toast.error(t('failed_added_user_room'), )
          }
        }}
        onRemoveAlt = {async (user: User) => {
          try {
            await user.unlinkSpace(authStore.getCurRoomID);
            toast.success(t('removed_user_room'));
          } catch (err) {
            toast.error(t('failed_removed_user_room'))
          }
        }}
      />
      <Modal show={showConfirmDialog}>
        <ConfirmationDialog
          confirmButtonLabel = {t('confirm')}
          onCancel  = {() => {setShowConfirmDialog(false); toLink.current = [];}}
          label     = {t("confirm_action")}
          message   = {t("confirm_action_add_additional_users_message_room")}
          content   = {
            <ul>
              {toLink.current.map(id => (
                <li key={id}>
                  <Text size="small" color={theme.color.slate12}>
                  {userStore.getItemByID(id)?.getFullName}
                  </Text>
                  </li>
              ))}
            </ul>
          }
          onConfirm = {async () => {
            for(const id of toLink.current) {
              // Link the group and all missing users to that project
              const user   = userStore.getItemByID(id);
              const status = user.getStatus(authStore.getCurProjectID);
              let   role   = user.getRole(authStore.getCurProjectID);

              if( role == UserRole.Owner ) role = UserRole.Leader;
              if( user.type === UserType.Group ) role = UserRole.Member;

              await user.linkSpace(authStore.getCurRoomID, SpaceType.Room, role, status);
              user.type === UserType.Group ?
                toast.success(t('added_group_project', {name: user.getFullName})) :
                toast.success(t('added_user_project',  {name: user.getFullName}));
            }
            toLink.current = [];
            setShowConfirmDialog(false);
          }}
        />
      </Modal>
      {/* Room Section */}
      <Section
        currentUser = {currentUser}
        data       = {rooms}
        addData    = {projRooms}
        title      = {t("meeting_sub_room_plural")}
        newToolTip = {t("tooltip_new_submeeting_room")}
        newIcon    = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="room" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        Card       = {RoomCard}
        onNew      = {async () => {
          const space = Space.create({
            title:        `${t("room")}`,
            type:         SpaceType.Room,
            parent:       room.id,
            workspace_id: room.workspace_id,
            project_id:   room.project_id,
          });
          try {
            await space.save();
            spaceStore.setActive(space);
            toast.success(t('created_room'));
          } catch (err) {
            toast.error(t('failed_created_room'))
          }
        }}
        onAdd    = {async (r: Space) => r.set("parent", room.id, true)}
        onRemove = {async (r: Space) => {
          try {
            await r.set("parent", room.project_id, true);
            spaceStore.setActive(null);
          } catch (err) {
            console.error("Failed to unlink subroom", room)
          }
        }}
      />
      {/* Tag Section */}
      <Section
        currentUser = {currentUser}
        data       = {roomTags}
        addData    = {tags}
        space_id   = {authStore.getCurRoomID}
        title      = {t("room_tag_plural")}
        newToolTip = {t("tooltip_new_tag")}
        newIcon    = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="tag" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        Card       = {TagCard}
        onNew      = {async () => {
          const tag = Tag.create({
            title:    `${t("tag")}`,
            workspace_id: authStore.getCurWorkspaceID,
          });
          try {
            await tag.save();
            await tag.linkSpace(authStore.getCurProjectID);
            await tag.linkSpace(authStore.getCurRoomID);
            toast.success(`${t('new_tag_created')}: ${tag.title}`)
          } catch(error) {
            toast.error(`${t('failed_to_create_tag')}`)
          }
        }}
        onAdd = {async (tag: Tag) => {
          try {
            // await tag.linkSpace(authStore.getCurProjectID);
            await tag.linkSpace(authStore.getCurRoomID);
            toast.success(`${t('add_tag')}: ${tag.title}`)
          } catch(error) {
            toast.error(`${t('failed_add_tag')}`)
          }
        }}
        onRemove = {async (tag: Tag) => { 
          try {
            await tag.unlinkSpace(authStore.getCurRoomID);
            toast.success(`${t('remove_tag')}: ${tag.title}`)
          } catch(error) {
            toast.error(`${t('failed_remove_tag')}`)
          }
        }}
      />
    </Container>
  );
}));